import type * as components from "@/components";
import type * as schemas from "@/schemas/sections";

// Single source of truth for the section-type -> {schema, component} pairing.
// Both imports above are TYPE-ONLY and erased at compile time, so this file
// has zero runtime cost and is safe for the client bundle even though it
// references every section schema name — tsc still validates every schema
// and component name against the real barrels (a typo'd name fails
// `tsc:check`). `registry/sections.ts` (server/build-only) derives
// SECTION_SCHEMAS from entries carrying a `schema`; `registry/section-
// components.ts` (client, schema-free) derives `sectionRegistry` from
// entries carrying a `component`. A single-sided entry is intentional: kit
// sections have a schema but are resolved by a different mechanism (the lazy
// section loader in `section-lazy-loaders.ts`, not the static component map).
export type SchemaKey = Extract<keyof typeof schemas, `${string}SectionSchema`>;
export type ComponentKey = keyof typeof components;

export interface SectionDef {
  schema?: SchemaKey;
  component?: ComponentKey;
}

// Declared once with `satisfies` so every literal schema/component name is
// checked against the real barrels at THIS declaration (the typo-guard); the
// exported `SECTION_MANIFEST` below is re-typed to a uniform
// `Record<SectionType, SectionDef>` so callers can access `.schema`/
// `.component` on any entry without a union-of-47-exact-shapes fight.
const manifest = {
  footer: { schema: "FooterSectionSchema", component: "FooterSection" },
  "lead-magnet-next-steps": {
    schema: "LeadMagnetNextStepsSectionSchema",
    component: "LeadMagnetNextSteps",
  },
  // Kit sections (ADR-0076): schema colocated with the component under
  // components/kit/<Name>/ (re-exported through the sections barrel), component
  // ALWAYS code-split via section-lazy-loaders.ts so kit JS/CSS never enters
  // the shared catch-all bundle — hence schema-only entries here.
  "kit-takeaways": { schema: "KitTakeawaysSectionSchema" },
  "kit-trainer": { schema: "KitTrainerSectionSchema" },
  "kit-about-tags": { schema: "KitAboutTagsSectionSchema" },
  "kit-quiz": { schema: "KitQuizSectionSchema" },
  "kit-navbar": { schema: "KitNavbarSectionSchema" },
  "kit-footer": { schema: "KitFooterSectionSchema" },
  "kit-guide-hero": { schema: "KitGuideHeroSectionSchema" },
  "kit-inside-showcase": { schema: "KitInsideShowcaseSectionSchema" },
  "kit-guide-services": { schema: "KitGuideServicesSectionSchema" },
  "kit-faq": { schema: "KitFaqSectionSchema" },
  "kit-guide-final-cta": { schema: "KitGuideFinalCtaSectionSchema" },
  // Homepage kit family (ADR-0076 homepage convergence): the `home-v2-page`
  // monolith converted to a pure-JSON `kit-home-*` composition. Schema-only,
  // same as every other kit section — code-split via section-lazy-loaders.ts.
  "kit-home-hero": { schema: "KitHomeHeroSectionSchema" },
  "kit-home-pain": { schema: "KitHomePainSectionSchema" },
  "kit-home-services": { schema: "KitHomeServicesSectionSchema" },
  // Generic (not homepage-specific) — thin adapter over the shared
  // `guide/GuideReviewsCarousel` standardized social-proof category
  // (ADR-0065); reused by future non-homepage pages too.
  "kit-reviews": { schema: "KitReviewsSectionSchema" },
  // Interview sales-page family (ADR-0076 convergence): the `InterviewPage`
  // monolith converted to a pure-JSON `kit-*` composition. Each is a thin
  // adapter over its still-TSX-composable `guide/Guide*` namesake (the same
  // "second, lighter mechanism" as the guide lead-magnet family) — schema-only,
  // code-split via section-lazy-loaders.ts like every other kit section.
  "kit-market-stat": { schema: "KitMarketStatSectionSchema" },
  // Top-8 v3 webinar landing (pain-led): compact «Ошибка №N до→после» formula
  // teaser. Focused single-purpose section (no existing before/after fit).
  "kit-error-teaser": { schema: "KitErrorTeaserSectionSchema" },
  "kit-offer-formats": { schema: "KitOfferFormatsSectionSchema" },
  "kit-cross-sell": { schema: "KitCrossSellSectionSchema" },
  // Résumé sales-page family (ADR-0076 convergence): the `ResumeGuidePage`
  // monolith converted to a pure-JSON `kit-*` composition. Each is a thin
  // adapter over its still-TSX-composable `guide/Guide*` namesake (the same
  // pattern as the interview family) — schema-only, code-split via
  // section-lazy-loaders.ts like every other kit section. `kit-service-tiers`
  // is the checkout owner: it calls the GLOBAL `useCheckout()` drawer
  // directly (not a bespoke modal or cross-section event bus).
  "kit-hero-portrait": { schema: "KitHeroPortraitSectionSchema" },
  "kit-before-after": { schema: "KitBeforeAfterSectionSchema" },
  "kit-service-tiers": { schema: "KitServiceTiersSectionSchema" },
  "kit-checklist-island": { schema: "KitChecklistIslandSectionSchema" },
  "kit-final-panel": { schema: "KitFinalPanelSectionSchema" },
  // Bespoke-but-schema-validated (the ADR-0084 path after full-page custom
  // components were retired): page-specific design work ships as a kit section
  // with all copy in JSON, not as a `type:"custom"` monolith.
  "kit-gift-island": { schema: "KitGiftIslandSectionSchema" },
  // Generic (not page-specific) kit sections (ADR-0076 dreamjob-weekend
  // convergence): net-new canonical categories, not thin adapters over a
  // `guide/Guide<Name>` namesake — reusable by any future course/sales page.
  "kit-benefits": { schema: "KitBenefitsSectionSchema" },
  "kit-stats": { schema: "KitStatsSectionSchema" },
  "kit-countdown": { schema: "KitCountdownSectionSchema" },
  "kit-program": { schema: "KitProgramSectionSchema" },
  "kit-process": { schema: "KitProcessSectionSchema" },
  "kit-case": { schema: "KitCaseSectionSchema" },
  "kit-links": { schema: "KitLinksSectionSchema" },
  // Festive sale one-offs (birthday-2026, ADR-0076 §5 bespoke escape hatch):
  // disposable promo sections — delete or promote after the sale window.
  "kit-sale-hero": { schema: "KitSaleHeroSectionSchema" },
  "kit-sale-tiers": { schema: "KitSaleTiersSectionSchema" },
  "kit-sale-bonus": { schema: "KitSaleBonusSectionSchema" },
  "kit-sale-leadmagnet": { schema: "KitSaleLeadMagnetSectionSchema" },
  // Utility/legal pages (docs/oferta/policy, ADR-0076 convergence): minimal
  // centered hero + a react-markdown content body. kit-markdown keeps the
  // react-markdown/remark-gfm rendering base (moved from the classic
  // `markdown-content` section) — code-split via section-lazy-loaders.ts
  // like every other kit section.
  "kit-hero-simple": { schema: "KitHeroSimpleSectionSchema" },
  "kit-markdown": { schema: "KitMarkdownSectionSchema" },
  "thank-you-redirect": {
    schema: "ThankYouRedirectSectionSchema",
    component: "ThankYouRedirect",
  },
} satisfies Record<string, SectionDef>;

export type SectionType = keyof typeof manifest;

// The exact subset of ComponentKey/SchemaKey actually referenced by the
// manifest above (derived from the narrow `manifest`, not the widened
// `SECTION_MANIFEST` below) — lets section-components.ts's hand-imported
// `COMPONENTS` map be typed to require exactly those keys, no more, no less.
// The conditional (rather than direct `["component"]`/`["schema"]` indexing)
// distributes over the per-entry literal union instead of erroring on the
// entries that lack one side.
type ManifestValue = (typeof manifest)[keyof typeof manifest];
export type UsedComponentKey = ManifestValue extends { component: infer C extends string }
  ? C
  : never;
export type UsedSchemaKey = ManifestValue extends { schema: infer S extends string } ? S : never;

export const SECTION_MANIFEST: Record<SectionType, SectionDef> = manifest;
